/**
 * useAccessibilityPreferences Hook - Wrapper with CSS application and debouncing
 *
 * This hook wraps the API layer and adds:
 * - CSS class application to document.body
 * - Debounced auto-save (500ms delay)
 *
 * The API layer (get-accessibility-preferences.ts, update-accessibility-preferences.ts)
 * handles pure data fetching and mutation logic.
 */

import { useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { useCurrentUser } from '@/lib/auth';
import {
  useAccessibilityPreferencesQuery,
  type AccessibilityPreferences,
} from '../api/get-accessibility-preferences';
import { useUpdateAccessibilityPreferences } from '../api/update-accessibility-preferences';
import { profileQueryKeys } from '../api/query-keys';
import { DEFAULT_ACCESSIBILITY_PREFERENCES } from '../api/types';

// Re-export the type for consumers of this hook
export type { AccessibilityPreferences };

/**
 * Apply CSS classes to document.body based on visual preferences
 */
const applyAccessibilityClasses = (preferences: AccessibilityPreferences) => {
  const body = document.body;

  // Visual preferences that affect styling
  const classMap: Record<string, boolean> = {
    dyslexic: preferences.dyslexia_friendly_font,
    'high-contrast': preferences.high_contrast_mode,
    'large-text': preferences.large_text_mode,
    'reduced-motion': preferences.reduced_motion,
  };

  // Apply/remove visual preference classes
  Object.entries(classMap).forEach(([className, enabled]) => {
    if (enabled) {
      body.classList.add(className);
    } else {
      body.classList.remove(className);
    }
  });
};

/**
 * Hook for managing user accessibility preferences
 *
 * Wraps the API layer with:
 * - CSS class application for visual preferences
 * - Debounced auto-save with 500ms delay
 * - Optimistic updates for immediate UI feedback
 */
export const useAccessibilityPreferences = () => {
  const { id } = useCurrentUser();
  const userId = id ?? '';
  const queryClient = useQueryClient();

  // Use API layer for fetching
  const { data: preferences, isLoading } = useAccessibilityPreferencesQuery({
    userId,
    queryConfig: {
      enabled: !!userId,
    },
  });

  // Use API layer for updating (without toast - we handle debouncing)
  const mutation = useUpdateAccessibilityPreferences({
    userId,
    mutationConfig: {
      // Override the default toast behavior since we debounce
      // The API layer's success toast will still fire
    },
  });

  // Create debounced update function (500ms delay)
  // Memoize to prevent recreation on every render
  const debouncedUpdate = useMemo(
    () =>
      debounce((updates: Partial<AccessibilityPreferences>) => {
        if (!userId) return;
        mutation.mutate({ userId, preferences: updates });
      }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedUpdate.cancel();
    };
  }, [debouncedUpdate]);

  // Update individual preference with optimistic CSS + debounced save
  const updatePreference = useCallback(
    <K extends keyof AccessibilityPreferences>(
      key: K,
      value: AccessibilityPreferences[K]
    ) => {
      // Optimistically update local state and apply CSS immediately
      const currentPrefs = preferences ?? DEFAULT_ACCESSIBILITY_PREFERENCES;
      const updated = { ...currentPrefs, [key]: value } as AccessibilityPreferences;
      queryClient.setQueryData(profileQueryKeys.accessibility(userId), updated);
      applyAccessibilityClasses(updated);

      // Trigger debounced save
      debouncedUpdate({ [key]: value });
    },
    [preferences, userId, queryClient, debouncedUpdate]
  );

  // Apply CSS classes when preferences load or change
  useEffect(() => {
    if (preferences) {
      applyAccessibilityClasses(preferences);
    }
  }, [preferences]);

  return {
    preferences: preferences ?? DEFAULT_ACCESSIBILITY_PREFERENCES,
    isLoading,
    updatePreference,
    isSaving: mutation.isPending,
  };
};
